/**
 * Theme tokens for Sill's dashboard and marketing surfaces.
 *
 * Values are byte-for-byte from `docs/reference/FoyerLanding.jsx` lines 17–39
 * (the canonical baseline per CLAUDE.md §4), with `ink` and `paper` added
 * from `docs/reference/Foyer_Dashboard_CURRENT.jsx` lines 35 / 49 to take the
 * union of keys used by both apps. `ink` and `paper` are intentionally
 * identical across themes — they're inverted accents for badges that need
 * dark-on-light contrast regardless of the active theme.
 *
 * `darkC` is the canonical shape; `ThemeTokens = typeof darkC` and `lightC`
 * mirrors it key-for-key.
 */
export const darkC = {
    name: 'dark',
    bg: '#0A0A0A',
    surface: '#141312',
    surface2: '#1B1A18',
    surface3: '#221F1C',
    border: '#2A2724',
    borderSoft: '#1F1D1A',
    text: '#F2EEE8',
    body: '#D9D2C5',
    muted: '#867E72',
    dim: '#4A463F',
    ink: '#0A0A0A',
    paper: '#FAF7F0',
    lime: '#D9FF3F',
    limeFg: '#D9FF3F',
    limeDim: 'rgba(217,255,63,0.12)',
    amber: '#FFB627',
    amberFg: '#FFB627',
    amberDim: 'rgba(255,182,39,0.10)',
    coral: '#FF6B47',
    coralFg: '#FF6B47',
    coralDim: 'rgba(255,107,71,0.10)',
    sky: '#7FB8FF',
    skyFg: '#7FB8FF',
    skyDim: 'rgba(127,184,255,0.10)',
    mint: '#5AFFCD',
    mintFg: '#5AFFCD',
    mintDim: 'rgba(90,255,205,0.10)',
};
export const lightC = {
    name: 'light',
    bg: '#FAF7F0',
    surface: '#FFFFFF',
    surface2: '#F2EEE5',
    surface3: '#E8E2D4',
    border: '#D8D1BE',
    borderSoft: '#E8E2D2',
    text: '#1A1410',
    body: '#3D3329',
    muted: '#6B5F4F',
    dim: '#A89E89',
    ink: '#0A0A0A',
    paper: '#FAF7F0',
    lime: '#D9FF3F',
    limeFg: '#3D5500',
    limeDim: 'rgba(61,85,0,0.08)',
    amber: '#FFB627',
    amberFg: '#8C5A00',
    amberDim: 'rgba(140,90,0,0.08)',
    coral: '#FF6B47',
    coralFg: '#A0331E',
    coralDim: 'rgba(160,51,30,0.08)',
    sky: '#7FB8FF',
    skyFg: '#1F4A85',
    skyDim: 'rgba(31,74,133,0.08)',
    mint: '#5AFFCD',
    mintFg: '#0F6B52',
    mintDim: 'rgba(15,107,82,0.08)',
};
/**
 * Brand tokens — visual-identity layer (ADR-0012).
 *
 * Additive to `darkC` / `lightC`: where an existing palette entry already
 * carries the right value, `brand` aliases it instead of duplicating, keeping
 * a single source of truth. The genuinely new values are:
 *   - `accentDim`: a darker lime suitable for light-theme text/icon use where
 *     `lime` (#D9FF3F) fails contrast.
 *   - `textOnDark` / `textOnLight`: warm off-white / near-black for the
 *     wordmark glyphs (slightly warmer than `darkC.text` / `lightC.text` —
 *     the wordmark is rendered in Instrument Serif at display sizes and the
 *     warmer tone reads cleaner there).
 *
 * The mark SVGs use `currentColor` on the door posts so the surrounding text
 * color (driven by the theme's `--text` CSS variable) themes them
 * automatically; these constants exist for code paths that need explicit
 * hex (e.g. canvas, OG images).
 *
 * The product-identifier rename (Foyer → Sill) is **not** part of this
 * token introduction. ADR-0012 introduces the visual-identity primitive;
 * ADR-0013 carries the rename. This object survives both ADRs unchanged
 * because the visual layer was decoupled from the identifier sweep.
 */
export const brand = {
    accent: '#D9FF3F', // = darkC.lime — primary accent, the sill in the mark
    accentInk: '#0A0A0A', // = darkC.ink — ink to use ON lime backgrounds
    accentDim: '#B8A800', // new — lime equivalent for light theme (better contrast)
    textOnDark: '#EDEAE0', // wordmark color in dark theme
    textOnLight: '#1A1A18', // wordmark color in light theme
    groundDark: '#0A0A0A', // = darkC.bg — near-black ground
    groundLight: '#F5F3EC', // warm off-white ground (paired with brand wordmark)
};
//# sourceMappingURL=tokens.js.map